import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, Link, useLocation } from 'react-router-dom';
import api from '../api';

// Logo always loaded from /logo.png in public folder
const LOGO_URL = '/logo.png';

function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen]         = useState(false);
  const [settings, setSettings] = useState({});
  const [logoOk, setLogoOk]     = useState(true);
  const location = useLocation();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);
  useEffect(() => { setOpen(false); }, [location]);
  useEffect(() => {
    api.get('/settings').then(r => setSettings(r.data.data || {})).catch(() => {});
  }, []);

  const siteName = settings.site_name || 'Bacha & Son';
  const [brand, accent] = siteName.includes(' ')
    ? [siteName.split(' ')[0], ' ' + siteName.split(' ').slice(1).join(' ')]
    : [siteName, ''];

  const navLinks = [['/', 'Home'], ['/projects', 'Projects'], ['/services', 'Services'], ['/about', 'About'], ['/contact', 'Contact']];

  return (
    <nav className={`site-navbar ${scrolled || open ? 'scrolled' : ''}`}>
      <div className="container">
        <div className="d-flex align-items-center justify-content-between">

          {/* Brand — always shows logo + name together */}
          <Link to="/" className="text-decoration-none">
            <div className="navbar-brand-wrap">
              {logoOk && (
                <img src={LOGO_URL} alt={siteName} className="navbar-brand-logo"
                  onError={() => setLogoOk(false)} />
              )}
              <span>
                {brand}<span>{accent}</span>
              </span>
            </div>
          </Link>

          {/* Desktop nav */}
          <div className="d-none d-lg-flex align-items-center gap-1">
            {navLinks.map(([to, label]) => (
              <NavLink key={to} to={to} end={to === '/'}
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
                {label}
              </NavLink>
            ))}
          </div>

          {/* Mobile toggle */}
          <button className="d-lg-none" onClick={() => setOpen(!open)}
            style={{ background: 'none', border: '1.5px solid rgba(44,40,64,0.2)', color: 'var(--text)', padding: '7px 12px', borderRadius: 8, cursor: 'pointer' }}>
            <i className={`bi ${open ? 'bi-x-lg' : 'bi-list'}`} style={{ fontSize: 18 }} />
          </button>
        </div>

        {/* Mobile menu */}
        {open && (
          <div style={{ padding: '18px 0', borderTop: '1px solid rgba(44,40,64,0.08)', marginTop: 10, background: 'rgba(237,242,247,0.97)', borderRadius: '0 0 12px 12px' }}>
            {navLinks.map(([to, label]) => (
              <NavLink key={to} to={to} end={to === '/'}
                className={({ isActive }) => `nav-link d-block py-2 ${isActive ? 'active' : ''}`} style={{ color: 'var(--text-2)' }}>
                {label}
              </NavLink>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
}

function Footer() {
  const [settings, setSettings] = useState({});
  const [logoOk, setLogoOk]     = useState(true);
  useEffect(() => { api.get('/settings').then(r => setSettings(r.data.data || {})).catch(() => {}); }, []);

  const s = settings;
  const siteName = s.site_name || 'Bacha & Son Construction';
  const [brand, accent] = siteName.includes(' ')
    ? [siteName.split(' ')[0], ' ' + siteName.split(' ').slice(1).join(' ')]
    : [siteName, ''];

  return (
    <footer className="site-footer">
      <div className="container">
        <div className="row g-5">
          <div className="col-lg-4">
            {/* Logo + name */}
            <div className="footer-logo">
              {logoOk && (
                <img src={LOGO_URL} alt={siteName} className="footer-logo-img"
                  onError={() => setLogoOk(false)} />
              )}
              <span>{brand}<span>{accent}</span></span>
            </div>
            <p style={{ color: 'rgba(242,234,224,0.45)',fontSize: '0.9rem', marginBottom: 0 }}>
              {s.site_tagline || 'Building Excellence, Crafting Legacies'}
            </p>
          </div>
          <div className="col-lg-2 col-6">
            <h6 style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: 'rgba(255,255,255,0.28)', marginBottom: 18 }}>Navigation</h6>
            {[['/', 'Home'], ['/projects', 'Projects'], ['/services', 'Services'], ['/about', 'About'], ['/contact', 'Contact Us']].map(([to, label]) => (
              <Link key={to} to={to} className="d-block mb-2" style={{ color: 'rgba(242,234,224,0.4)', fontSize: '0.875rem', transition: 'var(--transition)' }}
                onMouseEnter={e => e.target.style.color='var(--c3-light)'}
                onMouseLeave={e => e.target.style.color='rgba(242,234,224,0.4)'}
              >{label}</Link>
            ))}
          </div>
          <div className="col-lg-3 col-6">
            <h6 style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: 'rgba(255,255,255,0.28)', marginBottom: 18 }}>Services</h6>
            {['General Construction','Architectural Design','Interior Renovation','Project Management','Structural Engineering'].map(sv => (
              <Link key={sv} to="/services" className="d-block mb-2" style={{ color: 'rgba(242,234,224,0.4)', fontSize: '0.875rem', transition: 'var(--transition)' }}
                onMouseEnter={e => e.target.style.color='var(--c3-light)'}
                onMouseLeave={e => e.target.style.color='rgba(242,234,224,0.4)'}
              >{sv}</Link>
            ))}
          </div>
          <div className="col-lg-3">
            <h6 style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: 'rgba(255,255,255,0.28)', marginBottom: 18 }}>Contact</h6>
            {[
              { icon: 'bi-geo-alt',   val: s.site_address },
              { icon: 'bi-telephone', val: s.site_phone },
              { icon: 'bi-envelope',  val: s.site_email },
            ].filter(c => c.val).map(c => (
              <p key={c.icon} style={{ color: 'rgba(242,234,224,0.45)', fontSize: '0.875rem', marginBottom: 12 }}>
                <i className={`bi ${c.icon} me-2`} style={{ color: 'var(--c3-light)' }} />{c.val}
              </p>
            ))}
            <div className="footer-social d-flex gap-2" style={{ marginTop: 18 }}>
              {s.facebook_url  && <a href={s.facebook_url}  target="_blank" rel="noreferrer"><i className="bi bi-facebook" /></a>}
              {s.instagram_url && <a href={s.instagram_url} target="_blank" rel="noreferrer"><i className="bi bi-instagram" /></a>}
              {s.linkedin_url  && <a href={s.linkedin_url}  target="_blank" rel="noreferrer"><i className="bi bi-linkedin" /></a>}
              {s.twitter_url   && <a href={s.twitter_url}   target="_blank" rel="noreferrer"><i className="bi bi-twitter-x" /></a>}
            </div>
          </div>
        </div>
      </div>
      <div className="footer-bottom">
        <div className="container d-flex flex-wrap align-items-center justify-content-between gap-3">
          <p style={{ color: 'rgba(242,234,224,0.25)', fontSize: '0.8rem', margin: 0 }}>
            © {new Date().getFullYear()} {siteName}. All rights reserved.
          </p>
          <p style={{ color: 'rgba(242,234,224,0.25)', fontSize: '0.8rem', margin: 0 }}>
           Developed By: talhayaseentdr@gmail.com
          </p>
        </div>
      </div>
    </footer>
  );
}

export default function SiteLayout() {
  return (
    <>
      <Navbar />
      <main style={{ minHeight: '100vh' }}>
        <Outlet />
      </main>
      <Footer />
    </>
  );
}
